import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLoadingStore } from '@/stores/loading'
import AppLayout from '@/components/layout/AppLayout.vue'
import LoginView from '@/views/LoginView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import EmployeeView from '@/views/EmployeeView.vue'
import ModuleView from '@/views/ModuleView.vue'
import DashboardView from '@/views/DashboardView.vue'
import ManageExamsView from '@/views/ManageExamsView.vue'
import ExamResultsView from '@/views/ExamResultsView.vue'
import { Users, BookOpen, LayoutDashboard, ClipboardList, Award } from 'lucide-vue-next'

// Types
interface NavigationItem {
  name: string
  to: string
  icon: any
}

interface RouteMeta {
  requiresAuth?: boolean
  title?: string
  icon?: any
  adminOnly?: boolean
  employeeOnly?: boolean
  hideFromSidebar?: boolean
}

// Route Configuration
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    redirect: '/dashboard', // Default to dashboard for all users
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: DashboardView,
        meta: {
          requiresAuth: true,
          title: 'Dashboard',
          icon: LayoutDashboard
        }
      },
      {
        path: 'employee',
        name: 'employee',
        component: EmployeeView,
        meta: {
          requiresAuth: true,
          title: 'Employees',
          icon: Users,
          adminOnly: true
        }
      },
      {
        path: 'module',
        name: 'module',
        component: ModuleView,
        meta: {
          requiresAuth: true,
          title: 'Modules',
          icon: BookOpen,
          adminOnly: true
        }
      },
      {
        path: 'my-modules',
        name: 'my-modules',
        component: () => import('@/views/MyModulesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Modules',
          icon: BookOpen,
          employeeOnly: true
        }
      },
      {
        path: 'take-exam/:examId',
        name: 'take-exam',
        component: () => import('@/views/TakeExamView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Take Exam',
          employeeOnly: true
        }
      },
      {
        path: 'module-preview/:moduleId',
        name: 'module-preview',
        component: () => import('@/views/ModulePreviewView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Module Preview',
          employeeOnly: true
        }
      },
      {
        path: 'exam',
        name: 'exam',
        component: ManageExamsView,
        meta: {
          requiresAuth: true,
          title: 'Exams',
          icon: ClipboardList,
          adminOnly: true
        }
      },
      {
        path: 'exam-results',
        name: 'exam-results',
        component: ExamResultsView,
        meta: {
          requiresAuth: true,
          title: 'Exam Results',
          icon: ClipboardList,
          adminOnly: true
        }
      },
      {
        path: 'certificates',
        name: 'certificates',
        component: () => import('@/views/CertificatesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Certificates',
          icon: Award,
          adminOnly: true
        }
      },
      {
        path: 'certificates/:id',
        name: 'view-certificate-admin',
        component: () => import('@/views/ViewCertificateView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          title: 'View Certificate',
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'certificate-template',
        name: 'certificate-template',
        component: () => import('@/views/CertificateView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Certificate Template',
          icon: Award,
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/exam-results/:id',
        name: 'ResultsExamView',
        component: () => import('@/views/ResultsExamView.vue'),
        props: true
      },
      {
        path: 'my-exams',
        name: 'my-exams',
        component: () => import('@/views/MyExamsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Exams',
          icon: ClipboardList,
          employeeOnly: true
        }
      },
      {
        path: 'my-certificates',
        name: 'my-certificates',
        component: () => import('@/views/MyCertificatesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Certificates',
          icon: Award,
          employeeOnly: true
        }
      },
      {
        path: 'my-certificates/:id',
        name: 'view-certificate',
        component: () => import('@/views/ViewCertificateView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          title: 'View Certificate',
          employeeOnly: true
      ,    hideFromSidebar: true
        }
      },
      {
        path: '/my-exam-results/:examId/:id',
        name: 'MyExamResultsView',
        component: () => import('@/views/MyExamResultsView.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          title: 'Exam Result Details',
          employeeOnly: true,
          hideFromSidebar: true
        }
      }
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      title: 'Login'
    }
  },
  {
    path: '/500',
    name: 'server-error',
    component: () => import('@/views/ServerErrorView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: {
      requiresAuth: false,
      title: 'Not Found'
    }
  }
]

// Router Instance
const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
  routes
})

// Navigation Helper
export const getNavigationItems = (routes: RouteRecordRaw[], userType?: string): NavigationItem[] => {
  const layoutRoute = routes.find(route => route.path === '/')
  if (!layoutRoute?.children) return []

  return layoutRoute.children
    .filter(route => {
      const meta = route.meta as RouteMeta
      // Filter out routes that don't need to be in navigation
      if (!meta?.requiresAuth || route.name === 'not-found' || route.path === '/profile') {
        return false
      }
      // Skip routes explicitly hidden from sidebar
      if (meta?.hideFromSidebar) {
        return false
      }
      // Exclude the take-exam and module-preview routes from the sidebar navigation
      if (route.name === 'take-exam' || route.name === 'module-preview') {
        return false
      }
      // Filter based on user permissions
      if (meta.adminOnly && userType !== 'Admin') {
        return false
      }
      if (meta.employeeOnly && userType !== 'Employee') {
        return false
      }
      return true
    })
    .map(route => ({
      name: (route.meta as RouteMeta)?.title || String(route.name),
      to: `/${route.path}`,
      icon: (route.meta as RouteMeta)?.icon
    }))
}

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const loadingStore = useLoadingStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Start loading when navigating to a new page (but not on initial load)
  if (from.name !== undefined) {
    const pageTitle = (to.meta?.title as string) || 'Page'
    loadingStore.startPageLoading(`Loading ${pageTitle}...`)
  }

  // Always check authentication status first
  const isAuthenticated = await authStore.checkAuth()
  const userType = authStore.currentUser?.user_type_label

  // If user is authenticated and trying to access login page, redirect to home
  if (to.name === 'login' && isAuthenticated) {
    loadingStore.stopPageLoading()
    // All users go to dashboard page
    next({ name: 'dashboard' })
    return
  }

  // Check if route requires authentication
  if (requiresAuth && !isAuthenticated) {
    loadingStore.stopPageLoading()
    // Redirect to login if not authenticated
    next({ name: 'login' })
    return
  }

  // Check if route is admin-only and user is not admin
  const routeMeta = to.meta as RouteMeta
  if (routeMeta?.adminOnly && isAuthenticated) {
    if (userType !== 'Admin') {
      loadingStore.stopPageLoading()
      // Redirect non-admin users to dashboard
      next({ name: 'dashboard' })
      return
    }
  }

  // Check if route is employee-only and user is not employee
  if (routeMeta?.employeeOnly && isAuthenticated) {
    if (userType !== 'Employee') {
      loadingStore.stopPageLoading()
      // Redirect non-employee users to dashboard
      next({ name: 'dashboard' })
      return
    }
  }

  // Handle root path redirect based on user type
  if (to.path === '/' && isAuthenticated) {
    loadingStore.stopPageLoading()
    // All users go to dashboard page
    next({ name: 'dashboard' })
    return
  }

  // Handle non-admin users accessing employee page (default redirect) - redirect to dashboard
  if (to.name === 'employee' && isAuthenticated && (from.name === 'login' || from.path === '/' || from.name === undefined)) {
    if (userType !== 'Admin') {
      loadingStore.stopPageLoading()
      next({ name: 'dashboard' })
      return
    }
  }

  next()
})

router.afterEach(() => {
  const loadingStore = useLoadingStore()
  // Add a small delay to show the loader briefly for better UX
  setTimeout(() => {
    loadingStore.stopPageLoading()
  }, 500)
})

export default router